// The shared door to the native analysis handle for hover, completion,
// definition and the outline: one `AnalysisPool` over the main thread's
// `tooling` build (`nativeTooling.ts`), plus the request shape every service
// takes. A service never holds a handle across an `await`; it asks
// `withAnalysis` for one, answers from it synchronously, and the pool decides
// when the handle is disposed (`analysisPool.ts` lists the rules).

import type { Analysis, NativeModules } from '../native/hollowscript.js';
import { STUDIO_CAPABILITY_NAMES, type StudioCapabilityName } from '../capabilityNames.js';
import { buildNativeCapabilityDeclarations } from '../nativeCapabilities.js';
import { getMonacoTooling, type MonacoTooling } from '../nativeTooling.js';
import { AnalysisPool, type AnalysisInput } from './analysisPool.js';

/** Default entry path for a source with no real file identity of its own
 * (DockConsole's REPL buffer). A named script file passes its own path
 * instead, so diagnostics and definitions identify which file they are
 * against once more than one is open. */
export const DEFAULT_ENTRY_PATH = 'console.hws';

export const NO_MODULES: NativeModules = Object.freeze({});

/** What a service needs to ask a question about one document. */
export interface AnalysisRequest {
  readonly source: string;
  /** The entry module's canonical path; defaults to `DEFAULT_ENTRY_PATH`. */
  readonly entryPath?: string;
  /** The capabilities this project grants; `undefined` grants every v1 one. */
  readonly grantedCapabilities: ReadonlySet<StudioCapabilityName> | undefined;
  /** The other scripts an `import` resolves against, by canonical path
   * (`scriptModules.ts`'s `buildScriptModules`). Defaults to none. */
  readonly modules?: NativeModules;
  /** Which live handle this document owns. Defaults to the entry path, which
   * is right for anything with a unique one; an editor passes its model's
   * identity so two models never share a handle. */
  readonly key?: string;
}

/** Builds the pool input for `request`. */
export function buildAnalysisInput(request: AnalysisRequest): AnalysisInput {
  const granted = request.grantedCapabilities ?? new Set<StudioCapabilityName>(STUDIO_CAPABILITY_NAMES);
  return {
    source: request.source,
    entryPath: request.entryPath ?? DEFAULT_ENTRY_PATH,
    declarations: buildNativeCapabilityDeclarations(granted),
    modules: request.modules ?? NO_MODULES,
    declarationsKey: STUDIO_CAPABILITY_NAMES.filter((name) => granted.has(name)).join(','),
  };
}

let sharedPool: AnalysisPool | undefined;
let sharedPoolPromise: Promise<AnalysisPool> | undefined;

/** The one pool every editor service shares, created once the tooling wasm
 * has loaded. */
export function getSharedAnalysisPool(): Promise<AnalysisPool> {
  sharedPoolPromise ??= getMonacoTooling().then((tooling) => {
    sharedPool = new AnalysisPool(tooling);
    return sharedPool;
  });
  return sharedPoolPromise;
}

/** Runs `answer` against the analysis of `request`, returning `undefined` if
 * the module refused to analyse it. `answer` must use the handle
 * synchronously and not keep it. */
export async function withAnalysis<T>(request: AnalysisRequest, answer: (analysis: Analysis, tooling: MonacoTooling) => T): Promise<T | undefined> {
  const [tooling, pool] = await Promise.all([getMonacoTooling(), getSharedAnalysisPool()]);
  const input = buildAnalysisInput(request);
  const analysis = pool.acquire(request.key ?? input.entryPath, input);
  return analysis === undefined ? undefined : answer(analysis, tooling);
}

/** Checks `request` ahead of the first question about it, so hover and
 * completion after a pause answer from a handle that is already there. */
export async function warmAnalysis(request: AnalysisRequest): Promise<void> {
  await withAnalysis(request, () => undefined);
}

/** Disposes the handle `key` owns, if any. Synchronous, and a no-op before
 * the tooling build has loaded (nothing can be live then). */
export function releaseAnalysis(key: string): void {
  sharedPool?.release(key);
}
